import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useEmulationGuard } from "@/hooks/useEmulationGuard";
import { supabase } from "@/lib/supabase";
import { captureError } from "@/lib/sentry";
import { cn, preventEnterSubmit } from "@/lib/utils";
import { Loader2, Copy, Check } from "lucide-react";

const profileSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address"),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

export function PersonalSettings() {
  const [isLoading, setIsLoading] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [emailChangeMode, setEmailChangeMode] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [copiedUserId, setCopiedUserId] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const { isEmulating, blockIfEmulating } = useEmulationGuard();

  const copyUserId = async () => {
    if (!user?.id) return;
    await navigator.clipboard.writeText(user.id);
    setCopiedUserId(true);
    setTimeout(() => setCopiedUserId(false), 2000);
  };

  const form = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.user_metadata?.first_name || "",
      lastName: user?.user_metadata?.last_name || "",
      email: user?.email || "",
    },
  });

  const passwordForm = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    if (user) {
      form.reset({
        firstName: user.user_metadata?.first_name || "",
        lastName: user.user_metadata?.last_name || "",
        email: user.email || "",
      });
    }
  }, [user, form]);

  const onSubmit = async (data: z.infer<typeof profileSchema>) => {
    // Profile name updates are low-stakes and useful during emulation
    // (e.g., reproducing a display bug). Only block when the change
    // would update the email — that's identity-affecting.
    if (emailChangeMode && blockIfEmulating('Changing email address')) return;
    setIsLoading(true);
    try {
      const updates = {
        email: emailChangeMode ? data.email : user?.email,
        data: { 
          first_name: data.firstName,
          last_name: data.lastName,
        },
      };

      const { error } = await supabase.auth.updateUser(updates);
      if (error) throw error;

      toast({
        description: "Profile updated successfully",
      });
      
      setEditingField(null);
      setEmailChangeMode(false);
    } catch (error) {
      captureError(error as Error, { feature: 'profile-update' });
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onPasswordSubmit = async (data: z.infer<typeof passwordSchema>) => {
    if (blockIfEmulating('Changing the user\'s password')) return;
    setIsLoading(true);
    try {
      // First verify current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: data.currentPassword,
      });

      if (signInError) {
        toast({
          title: "Error",
          description: "Current password is incorrect",
          variant: "destructive",
        });
        return;
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: data.newPassword
      });

      if (error) throw error;

      toast({
        description: "Password updated successfully",
      });
      
      setShowPasswordDialog(false);
      passwordForm.reset();
    } catch (error) {
      captureError(error as Error, { feature: 'password-update' });
      toast({
        title: "Error",
        description: "Failed to update password",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!user?.email) return;
    
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/auth/callback`,
      });

      if (error) throw error;

      setShowPasswordDialog(false);
      toast({
        description: "Password reset link sent to your email",
      });
    } catch (error) {
      console.error('Error sending reset password email:', error);
      captureError(error as Error, { feature: 'password-reset-link' });
      toast({
        title: "Error",
        description: "Failed to send password reset link",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Personal Details</h2>
        <p className="text-muted-foreground">
          Manage your personal information
        </p>
      </div>

      <div className="space-y-8">
        {/* Profile Picture */}
        <div className="space-y-4">
          <div className="flex items-center gap-8">
            <Avatar className="h-24 w-24">
              <AvatarImage src={user?.user_metadata?.avatar_url} />
              <AvatarFallback className="text-2xl">
                {user?.email?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-1">
              <h3 className="font-medium">Profile picture</h3>
              <p className="text-sm text-muted-foreground">
                JPG, GIF or PNG. Max size 2MB
              </p>
              <Button variant="outline" size="sm">
                Choose file
              </Button>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} onKeyDown={preventEnterSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First name</FormLabel>
                    <FormControl>
                      <div
                        className={cn(
                          "relative group",
                          editingField === "firstName" ? "ring-2 ring-primary" : ""
                        )}
                      >
                        <Input
                          {...field}
                          className={cn(
                            "bg-transparent transition-colors",
                            editingField !== "firstName" && "border-transparent hover:bg-accent focus:bg-background",
                          )}
                          onFocus={() => setEditingField("firstName")}
                          onBlur={() => {
                            setEditingField(null);
                            form.handleSubmit(onSubmit)();
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last name</FormLabel>
                    <FormControl>
                      <div
                        className={cn(
                          "relative group",
                          editingField === "lastName" ? "ring-2 ring-primary" : ""
                        )}
                      >
                        <Input
                          {...field}
                          className={cn(
                            "bg-transparent transition-colors",
                            editingField !== "lastName" && "border-transparent hover:bg-accent focus:bg-background",
                          )}
                          onFocus={() => setEditingField("lastName")}
                          onBlur={() => {
                            setEditingField(null);
                            form.handleSubmit(onSubmit)();
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-2">
              <FormLabel>Email address</FormLabel>
              <div className="flex items-end gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <Input
                          {...field}
                          disabled={!emailChangeMode}
                          className={cn(
                            "bg-transparent transition-colors",
                            !emailChangeMode && "border-transparent",
                            emailChangeMode && "border-input"
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {emailChangeMode ? (
                  <div className="flex gap-2">
                    <Button 
                      type="submit"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save"
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setEmailChangeMode(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEmailChangeMode(true)}
                    disabled={isEmulating}
                    title={isEmulating ? 'Disabled while emulating user' : undefined}
                  >
                    Change email
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <FormLabel>Password</FormLabel>
              <div className="flex items-center gap-4">
                <Input
                  type="password"
                  value="••••••••"
                  disabled
                  className="flex-1 bg-transparent border-transparent"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPasswordDialog(true)}
                  disabled={isEmulating}
                  title={isEmulating ? 'Disabled while emulating user' : undefined}
                >
                  Change password
                </Button>
              </div>
            </div>
          </form>
        </Form>

        {/* Account ID - for API reference */}
        {user?.id && (
          <div className="pt-6 border-t">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground/60">Account ID:</span>
              <code className="text-xs text-muted-foreground/60 font-mono">{user.id}</code>
              <button
                onClick={copyUserId}
                className="p-1 hover:bg-accent rounded transition-colors"
                title="Copy Account ID"
              >
                {copiedUserId ? (
                  <Check className="h-3 w-3 text-green-500" />
                ) : (
                  <Copy className="h-3 w-3 text-muted-foreground/60" />
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>

          <Form {...passwordForm}>
            <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} onKeyDown={preventEnterSubmit} className="space-y-4">
              <FormField
                control={passwordForm.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current Password</FormLabel>
                    <FormControl>
                      <PasswordInput {...field} />
                    </FormControl>
                    <FormMessage />
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className="text-xs text-primary hover:underline mt-1"
                    >
                      Forgot your password?
                    </button>
                  </FormItem>
                )}
              />

              <FormField
                control={passwordForm.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <PasswordInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={passwordForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm New Password</FormLabel>
                    <FormControl>
                      <PasswordInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowPasswordDialog(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Password"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}